import React, { PropsWithChildren } from 'react';

import { useAppSelector } from 'state/hooks';

import { WorkflowTravelTimeToolbar } from 'features/bookmarks/WorkflowTravelTimeToolbar';
import { SavedRoutesVariant } from 'features/bookmarks/utils';
import { classNames } from 'features/common/utils';

import { MAXIMIZE_DATA } from '../../appConstants';
import { selectHideSidebar, selectMaximize } from '../../state/workflowSlice';
import { BookmarkToolbar } from '../bookmarks/BookmarkToolbar';
import { InfoWalkmeCrumb } from '../info_hover/InfoHover';
import RouteSegmentInfo from './SegmentInfo/RouteSegmentInfo';
import { SegmentInfo } from './SegmentInfo/SegmentInfo';
import TaskBarTopbar from './TaskBarTopbar';

import styles from './Taskbar.module.css';

export function TaskBarHeader({ children }: PropsWithChildren) {
  return <h2 className={styles.task_bar_header}>{children}</h2>;
}

export function TaskBarGroup({ children }: PropsWithChildren) {
  return <div className={styles.task_bar_group}>{children}</div>;
}

export function TaskBarComponent({
  children,
  centered = false,
  left = false,
  inline = false,
}: {
  centered?: boolean;
  left?: boolean;
  inline?: boolean;
} & PropsWithChildren) {
  let className = styles.task_bar_component;
  if (centered) {
    className = styles.task_bar_component_centered;
  } else if (left) {
    className = styles.task_bar_component_left;
  } else if (inline) {
    className = styles.task_bar_component_inline;
  }
  return <div className={className}>{children}</div>;
}

export function TaskBarSkeleton({
  upper,
  middle,
  lower,
  classes = [],
  renderSectionFiller = true,
  lowerClassName,
  showTitle = false,
  ignoreHideSidebar = false,
}: {
  upper: React.ReactNode;
  middle: React.ReactNode;
  lower: React.ReactNode;
  classes?: string[];
  renderSectionFiller?: boolean;
  /** optional class to override the wrapper around `lower` */
  lowerClassName?: string;
  showTitle?: boolean;
  ignoreHideSidebar?: boolean;
}) {
  const maximize = useAppSelector(selectMaximize);
  const isSidebarHidden = useAppSelector(selectHideSidebar);
  const collapseTaskBar = isSidebarHidden && !ignoreHideSidebar;
  const className = classNames(
    styles.task_bar,
    maximize === MAXIMIZE_DATA && styles.task_bar_hidden,
    collapseTaskBar && styles.task_bar_colapsed,
    ...classes,
  );
  return (
    <div className={className}>
      {showTitle && <TaskBarTopbar ignoreHideSidebar={ignoreHideSidebar} />}
      <div className={classNames(collapseTaskBar && styles.hidden)}>
        {upper}
      </div>

      {renderSectionFiller && (
        <div
          className={classNames(
            styles.task_bar_section_filler,
            collapseTaskBar && styles.hidden,
          )}
        />
      )}
      <div className={classNames(collapseTaskBar && styles.hidden)}>
        {middle}
      </div>
      <div
        className={classNames(
          lowerClassName || styles.task_bar_graph_controls,
          collapseTaskBar && styles.hidden,
        )}
      >
        {lower}
      </div>
    </div>
  );
}

export function ChartSettingsWrapper({
  children,
  extras,
}: {
  children: React.ReactNode;
  extras?: React.ReactNode;
}) {
  return (
    <>
      <TaskBarHeader>
        Chart Settings <InfoWalkmeCrumb elId="chart-settings" />
      </TaskBarHeader>
      <div className={styles.task_bar_section_body}>{children}</div>
      <div className={styles.task_bar_section_filler} />
      {extras}
    </>
  );
}

type SegmentInfoType = 'segment' | 'route' | undefined;

export function TaskBar({
  children: chartSettings,
  extras,
  segmentInfo = undefined,
  savedRoutes = undefined,
  showTitle = true,
  ignoreHideSidebar = false, //  When true, task bar stays always expanded
}: {
  children?: React.ReactNode;
  extras?: React.ReactNode;
  segmentInfo?: SegmentInfoType;
  savedRoutes?: SavedRoutesVariant;
  showTitle?: boolean;
  ignoreHideSidebar?: boolean;
}) {
  return (
    <TaskBarSkeleton
      showTitle={showTitle}
      upper={
        savedRoutes ? (
          <WorkflowTravelTimeToolbar
            variant={savedRoutes}
            fullHeight={!chartSettings}
          />
        ) : (
          <BookmarkToolbar />
        )
      }
      middle={
        segmentInfo && (
          <TaskBarGroup>
            <TaskBarHeader>
              {segmentInfo === 'route' ? 'Route ' : 'Segment '} information
            </TaskBarHeader>
            <div className={styles.task_bar_section_body}>
              {segmentInfo === 'route' ? <RouteSegmentInfo /> : <SegmentInfo />}
            </div>
          </TaskBarGroup>
        )
      }
      lower={
        chartSettings && (
          <ChartSettingsWrapper extras={extras}>
            {chartSettings}
          </ChartSettingsWrapper>
        )
      }
      ignoreHideSidebar={ignoreHideSidebar}
    />
  );
}
